import { findRegionTagByName, hasRegionTagLabel } from '../facets/region-tag-labels';

/**
 * Legacy "Remote - <scope>" pseudo-locations ("Remote - Global", "Remote - EMEA", "Etätyö - Suomi",
 * "Distans - Europa"). The word before the dash is the remoteness facet value in that locale;
 * the scope after it is a place ("EMEA", "Europe", "Finland") or means anywhere ("Global",
 * "Worldwide", "Anywhere", "Maailma", "Världen").
 */
const REMOTE_PSEUDO_LOCATION = /^(remote|etätyö|etä|distans|distansarbete)\s*(?:-|–|\(|,)?\s*(.*?)\)?\s*$/i;
const HYBRID_PSEUDO_LOCATION = /^(hybrid|hybridi|hybridityö|hybridarbete)\b/i;
const ANYWHERE_SCOPES = new Set(['', 'global', 'worldwide', 'anywhere', 'world', 'maailma', 'maailmanlaajuinen', 'världen', 'globalt', 'var som helst', 'missä tahansa']);

export interface DesiredLocationsShape {
  regionTags: string[];
  locations: string[];
  /**
   * Set when a legacy remote pseudo-location was consumed: the remoteness facet value it named,
   * in the locale it was written in ("Remote", "Etätyö", "Distans"). The caller adds it to
   * desiredJob.remoteness when that is still empty, so "Remote - EMEA" keeps meaning "remote,
   * anywhere in EMEA" after migration rather than just "EMEA".
   */
  impliedRemoteness: string | null;
}

/**
 * TEC-482 for the profile: desired locations live as gazetteer `regionTags` placeIds. Profiles
 * saved before that carry flat `location` strings ("Helsinki, Finland", "Remote - Global"), so
 * every read path and every write to either field runs through here:
 * - known placeIds are kept (unknown ids are dropped, they cannot be shown or filtered on),
 * - legacy strings that resolve to a place become regionTags,
 * - "Remote - <scope>" pseudo-locations become the scope's regionTag (EMEA, Europe, Finland...)
 *   plus the remote intent, reported as impliedRemoteness for the caller to put in desiredJob.remoteness,
 * - anything unresolvable stays in `locations` so nothing the user typed is lost.
 */
export function normalizeDesiredLocations(input: { regionTags?: unknown; locations?: unknown }): DesiredLocationsShape {
  const tags: string[] = [];
  const pushTag = (t: string) => {
    if (!tags.includes(t)) tags.push(t);
  };
  for (const raw of Array.isArray(input.regionTags) ? input.regionTags : []) {
    if (typeof raw === 'string' && hasRegionTagLabel(raw)) pushTag(raw);
  }
  const leftovers: string[] = [];
  let impliedRemoteness: string | null = null;
  for (const raw of Array.isArray(input.locations) ? input.locations : []) {
    if (typeof raw !== 'string') continue;
    const name = raw.trim();
    if (!name || HYBRID_PSEUDO_LOCATION.test(name)) continue;
    const remote = REMOTE_PSEUDO_LOCATION.exec(name);
    if (remote) {
      // "Remote - EMEA": remote (recorded once) anywhere in EMEA (a real regionTag).
      // "Remote - Global": remote with no place narrowing at all.
      impliedRemoteness = impliedRemoteness ?? remote[1];
      const scope = (remote[2] ?? '').trim();
      if (!ANYWHERE_SCOPES.has(scope.toLowerCase())) {
        const scopeTag = findRegionTagByName(scope);
        if (scopeTag) pushTag(scopeTag);
        else if (!leftovers.some((l) => l.toLowerCase() === scope.toLowerCase())) leftovers.push(scope);
      }
      continue;
    }
    const tag = findRegionTagByName(name);
    if (tag) pushTag(tag);
    else if (!leftovers.some((l) => l.toLowerCase() === name.toLowerCase())) leftovers.push(name);
  }
  return { regionTags: tags, locations: leftovers, impliedRemoteness };
}
